import React, { useLayoutEffect, useRef } from 'react';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { theme } from '../../../../../../../theme/phc';
import clsx from 'clsx';
import type { MedicalResultSource } from '../../../../../../../services/MedicalResultsService';

const useStyles = makeStyles(() => ({
  shadow: {
    // A background color is required for shadow to appear
    backgroundColor: theme.palette.divider,
    borderRadius: 6,
    position: 'relative',
    top: 10,
    left: 10,
  },
  rect: {
    height: 22,
    width: 42,
  },
  circle: {
    borderRadius: 7,
    height: 14,
    width: 14,
  },
}));

const DEFAULT_WIDTH = 30;
const DEFAULT_HEIGHT = 16;
export const TOOLTIP_HEIGHT = 60;
export const TOOLTIP_WIDTH = DEFAULT_WIDTH * 1.4;
export const TOOLTIP_X_OFFSET = DEFAULT_WIDTH * 0.2;

export type Datum = {
  x: Date;
  y: string | number;
  id: string | undefined;
  value: string | number;
  color: string;
  status?: 'optimal' | 'in-range' | 'attention' | 'default' | 'pending';
  source?: MedicalResultSource;
};

type Props = {
  x: number;
  y: number;
  animationIndex: number;
  selected: boolean;
  onClick: (datum: Datum) => void;
  datum: Datum;
  showValue: boolean;
};

export const ResultChartComponent = React.memo((props: Props) => {
  const { x, y, datum, selected, onClick } = props;
  const styles = useStyles();
  const containerRef = useRef<SVGGElement>(null);
  const isExtracted = datum.source === 'documentExtractionV2';

  const font = theme.typography.caption;

  useLayoutEffect(() => {
    if (selected && containerRef.current?.parentElement) {
      containerRef.current.parentElement.appendChild(containerRef.current);
    }
  }, [selected]);

  const handleClick = () => {
    onClick(datum as Datum);
  };

  return (
    <g
      ref={containerRef}
      transform={`translate(${x},${y})`}
      onClick={handleClick}
    >
      {selected && (
        <>
          <foreignObject x={-17} y={-17} width={32} height={38}>
            <div className={clsx(styles.shadow, styles.circle)} />
          </foreignObject>
          {isExtracted ? (
            <rect
              x={-4}
              y={-4}
              width={8}
              height={8}
              fill={datum.color}
              strokeWidth={0}
              transform="rotate(45)"
            />
          ) : (
            <circle r="4" fill={datum.color} strokeWidth={0} />
          )}
        </>
      )}
      {isExtracted ? (
        <rect
          x={selected ? -5 : -3}
          y={selected ? -5 : -3}
          width={selected ? 10 : 6}
          height={selected ? 10 : 6}
          fill={theme.palette.secondary.main}
          stroke={theme.palette.common.white}
          strokeWidth={selected ? 1 : 0}
          transform="rotate(45)"
        />
      ) : (
        <circle
          r={selected ? 5 : 2.5}
          fill={theme.palette.secondary.main}
          stroke={theme.palette.common.white}
          strokeWidth={selected ? 1 : 0}
        />
      )}
      {/* Create an empty Rect for hitSlop */}
      <rect
        x={-25}
        y={-45}
        fill={'transparent'}
        opacity={1}
        width={50}
        height={60}
        data-testid={
          selected
            ? `selected-result-chart-point-${datum.id}`
            : `not-selected-result-chart-point-${datum.id}`
        }
      />
      <g transform={`translate(${-DEFAULT_WIDTH / 2},${-2 * DEFAULT_HEIGHT})`}>
        {selected && (
          <foreignObject x={-16} y={-13} width={60} height={46}>
            <div className={clsx(styles.shadow, styles.rect)} />
          </foreignObject>
        )}
        {props.showValue && (
          <>
            <rect
              rx="6"
              ry="6"
              fill={datum.color}
              strokeWidth={0}
              x={selected ? -DEFAULT_WIDTH * 0.2 : 0}
              y={selected ? -DEFAULT_HEIGHT * 0.2 : DEFAULT_HEIGHT * 0.2}
              width={selected ? DEFAULT_WIDTH * 1.4 : DEFAULT_WIDTH}
              height={selected ? DEFAULT_HEIGHT * 1.4 : DEFAULT_HEIGHT}
            />

            <text
              x={DEFAULT_WIDTH / 2}
              y={selected ? DEFAULT_HEIGHT / 2 : DEFAULT_HEIGHT / 2 + 3}
              fill="white"
              textAnchor="middle"
              fontSize={font.fontSize}
              fontWeight={font.fontWeight}
              fontFamily={font.fontFamily}
              letterSpacing={font.letterSpacing}
              fontStyle={font.fontStyle}
              alignmentBaseline="central"
            >
              {datum.value}
            </text>
          </>
        )}
      </g>
    </g>
  );
});
